import { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import type { ScoringExplanation } from "../types";

/**
 * A 0–100 bar with the number beside it. Neutral colour: the number and words carry the meaning. The value is plain
 * text ("62 out of 100"), read in place after the part's label, rather than an ARIA meter that some screen readers skip.
 */
function ScoreBar({ score }: { score: number }) {
  const v = Math.max(0, Math.min(100, Math.round(score)));
  return (
    <span className="score-bar">
      <span className="score-bar__track" aria-hidden="true"><span className="score-bar__fill" style={{ width: `${v}%` }} /></span>
      <span className="score-bar__num num">{v}<span className="visually-hidden"> out of 100</span></span>
    </span>
  );
}

/** How the run is scored (08 §9), as the engine explains it. Shared by the in-play page and the outcome screen. */
export function ScoringDetails({ scoring, headingLevel = 3 }: { scoring: ScoringExplanation; headingLevel?: 3 | 4 }) {
  const H = headingLevel === 3 ? "h3" : "h4";
  return (
    <div className="scoring">
      {scoring.final !== null ? (
        <p className="scoring__final">
          <span className="scoring__final-num num">{scoring.final}</span>
          <span className="scoring__final-of"> out of 100</span>
          <span className="scoring__label">{scoring.label}</span>
        </p>
      ) : (
        <p className="scoring__final muted">
          The totals are worked out when the run ends. Some parts depend on things you can't see yet, such as how
          stretched people really are or problems nobody has found, so they stay hidden until then.
        </p>
      )}
      <p className="scoring__formula">{scoring.formula}</p>
      {scoring.sections.map((s) => (
        <div key={s.key} className="scoring__section">
          <H className="scoring__section-title">
            {s.title}{s.score !== null && <> <span className="num">{s.score}</span><span className="muted"> <span aria-hidden="true">/</span><span className="visually-hidden">out of</span> 100</span></>}
          </H>
          <p className="muted">{s.summary}</p>
          <ul className="scoring__parts">
            {s.parts.map((p) => (
              <li key={p.key} className="scoring__part">
                <div className="scoring__part-head">
                  <span className="scoring__part-label">{p.label}</span>
                  {p.score !== null
                    ? <ScoreBar score={p.score} />
                    : <span className="muted">Revealed when the run ends</span>}
                </div>
                <p className="scoring__explains">{p.explains}</p>
                {p.detail && <p className="scoring__detail">{p.detail}</p>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

/** The in-play scoring page, shown only after the player has chosen to reveal it for this run. */
export function ScoringPanel({ runId, onSignedOut }: { runId: string; onSignedOut: () => void }) {
  const [scoring, setScoring] = useState<ScoringExplanation | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Refreshed each time the panel opens, so it reflects the run as it stands now.
  useEffect(() => {
    let live = true;
    api.scoring(runId)
      .then((r) => { if (live) setScoring(r.scoring); })
      .catch((e: unknown) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 401) onSignedOut();
        else setError(e instanceof Error ? e.message : "Could not load the scoring.");
      });
    return () => { live = false; };
  }, [runId, onSignedOut]);

  return (
    <section className="panel" aria-labelledby="scoring-title" aria-busy={!scoring && !error}>
      <h2 id="scoring-title" className="panel-title">Scoring</h2>
      <p className="panel-note">How this run would be scored if it ended now. You revealed this page for this run.</p>
      {error && <p className="form__error" role="alert">{error}</p>}
      {!scoring && !error && <p className="muted">Working it out…</p>}
      {scoring && <ScoringDetails scoring={scoring} />}
    </section>
  );
}
